import type { MwdWerklijn } from '@/apps/mwd-v2/types/Domain';

import type { MiniMwdLegendItem, MiniMwdLegendRow } from './miniMwdTypes';

type BuildLegendRowsArgs = {
  hasLegend: boolean;
  useEenhedenMode: boolean;
  selectedWerklijnen: (MwdWerklijn | undefined)[];
  legendItems: MiniMwdLegendItem[] | undefined;
  selectedLegendIds: string[] | undefined;
};

/**
 * Builds the legend rows shown next to the mini-MWD chart. In eenheden mode the
 * loaded werklijnen are combined with any still-pending eenheden from the props;
 * otherwise the provided legendItems are mapped directly.
 */
export function buildLegendRows({
  hasLegend,
  useEenhedenMode,
  selectedWerklijnen,
  legendItems,
  selectedLegendIds,
}: BuildLegendRowsArgs): MiniMwdLegendRow[] {
  if (!hasLegend || !legendItems) return [];

  const checkedFor = (id: string) => selectedLegendIds?.includes(id) ?? false;

  if (useEenhedenMode) {
    const loaded: MiniMwdLegendRow[] = selectedWerklijnen.map((werklijn) => {
      const id = werklijn!.eenheid?.nummer;
      const fromProps = legendItems.find((item) => item.id === id);
      return {
        id,
        nummer: fromProps?.nummer ?? id,
        subtype: fromProps?.subtype ?? werklijn!.left?.details?.materieelSoortEersteInzet,
        height: werklijn!.height,
        disabled: fromProps?.disabled,
        checked: checkedFor(id),
        isIngeregeld: fromProps?.isIngeregeld,
        isExternIngeregeld: fromProps?.isExternIngeregeld,
      };
    });
    const loadedIds = new Set(loaded.map((row) => row.id));
    const pending: MiniMwdLegendRow[] = legendItems
      .filter((item) => !loadedIds.has(item.id))
      .map((item) => ({
        id: item.id,
        nummer: item.nummer,
        subtype: item.subtype,
        height: undefined,
        disabled: item.disabled,
        checked: checkedFor(item.id),
        isIngeregeld: item.isIngeregeld,
        isExternIngeregeld: item.isExternIngeregeld,
      }));
    return [...loaded, ...pending];
  }

  return legendItems.map((item) => ({
    id: item.id,
    nummer: item.nummer,
    subtype: item.subtype,
    height: undefined,
    disabled: item.disabled,
    checked: checkedFor(item.id),
    isIngeregeld: item.isIngeregeld,
    isExternIngeregeld: item.isExternIngeregeld,
  }));
}
